import React from "react";
import { Box, Paper, Typography, Stack, Divider, IconButton, alpha, useTheme, Chip } from "@mui/material";
import {
  AddCircleRounded,
  Assignment,
  ChatBubbleOutline,
  CalendarToday,
  History,
  Room,
  WarningAmber,
  LocalFlorist,
  FormatListBulleted,
  Home,
  Person,
  ChevronRight,
} from "@mui/icons-material";
import { NavLink, Outlet, useParams } from "react-router-dom";
import useClientDetails from "../../../../hooks/useClientDetails";
import AssessmentsTabs from "./AssessmentsTabs/AssessmentsTabs";
import ConsentTabs from "./ConsentTabs/ConsentTabs";
import RiskProfileTabs from "./RiskProfileTabs/RiskProfileTabs";
import ActiveCareTabs from "./ActiveCareTabs/ActiveCareTabs";
import MonitorCareTabs from "./MonitorCare/MonitorCareTabs";
import InactiveCareTabs from "./InactiveCareTabs/InactiveCareTabs";

const Assessments = () => {
  const { id: clientID } = useParams();
  const { clientDetails, fullName } = useClientDetails(clientID);
  const theme = useTheme();

  const actionLinks = [
    { to: `${clientID}/assessments/initial-assessment`, icon: <Assignment />, label: "Initial Assessment" },
    { to: `Observation/${clientID}`, icon: <Assignment />, label: "Observations" },
    { to: `${clientID}/communication/`, icon: <ChatBubbleOutline />, label: "Communication" },
    { to: `visit-analysis/${clientID}`, icon: <CalendarToday />, label: "Visit Plan" },
    { to: `care-plan-history/${clientID}`, icon: <History />, label: "History" },
    { to: `reviewed-careplans/${clientID}`, icon: <Home />, label: "Reviews" },
    { to: `body-map/${clientID}`, icon: <Room />, label: "Body Map" },
    { to: `conditions-and-alerts/${clientID}`, icon: <WarningAmber />, label: "Conditions & Alerts" },
    { to: `active-assessments/${clientID}`, icon: <FormatListBulleted />, label: "Assessments" },
  ];

  return (
    <Box sx={{ width: "100%", pb: 4 }}>
      <Paper
        elevation={0}
        sx={{
          mb: 3,
          p: 2.5,
          borderRadius: "16px",
          bgcolor: alpha(theme.palette.primary.main, 0.04),
          border: `1px solid ${alpha(theme.palette.primary.main, 0.08)}`,
        }}
      >
        <Stack direction="row" alignItems="center" justifyContent="space-between" flexWrap="wrap" spacing={2}>
          <Stack direction="row" alignItems="center" spacing={2}>
            <Box
              sx={{
                width: 48,
                height: 48,
                borderRadius: "12px",
                bgcolor: theme.palette.primary.main,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                color: "#fff",
              }}
            >
              <Person sx={{ fontSize: 28 }} />
            </Box>
            <Box>
              <Typography variant="h6" fontWeight={700} sx={{ color: "#1A1A2E" }}>
                {fullName || "Client Name"}
              </Typography>
              <Stack direction="row" spacing={1.5} alignItems="center" flexWrap="wrap">
                {clientDetails?.birthdate && (
                  <Typography variant="caption" sx={{ color: "#667085" }}>
                    DOB: {clientDetails.birthdate}
                  </Typography>
                )}
                {clientDetails?.nhs_number && (
                  <>
                    <Box sx={{ width: 3, height: 3, borderRadius: "50%", bgcolor: "#D0D5DD" }} />
                    <Typography variant="caption" sx={{ color: "#667085" }}>
                      NHS: {clientDetails.nhs_number}
                    </Typography>
                  </>
                )}
                <Chip
                  label="Active"
                  size="small"
                  sx={{
                    height: 20,
                    fontSize: "0.6rem",
                    fontWeight: 600,
                    bgcolor: "#D1FAE5",
                    color: "#039855",
                  }}
                />
              </Stack>
            </Box>
          </Stack>
          <Typography variant="caption" sx={{ color: "#667085" }}>
            For Resus / New POC
          </Typography>
        </Stack>
      </Paper>

      <Paper
        elevation={0}
        sx={{
          mb: 3,
          p: 2,
          borderRadius: "16px",
          border: `1px solid ${alpha(theme.palette.divider, 0.08)}`,
          bgcolor: "#fff",
        }}
      >
        <Stack direction="row" flexWrap="wrap" spacing={0.5}>
          {actionLinks.map((link) =>
            link.to === "#" ? (
              <Box
                key={link.label}
                sx={{
                  px: 1.5,
                  py: 0.75,
                  borderRadius: "8px",
                  display: "flex",
                  alignItems: "center",
                  gap: 0.5,
                  opacity: 0.6,
                }}
              >
                <IconButton color="primary" size="small" sx={{ p: 0.5 }}>
                  {link.icon}
                </IconButton>
                <Typography variant="caption" sx={{ fontWeight: 500 }}>
                  {link.label}
                </Typography>
              </Box>
            ) : (
              <NavLink
                key={link.label}
                to={`/${link.to}`}
                style={({ isActive }) => ({
                  textDecoration: "none",
                })}
              >
                <Box
                  sx={{
                    px: 1.5,
                    py: 0.75,
                    borderRadius: "8px",
                    display: "flex",
                    alignItems: "center",
                    gap: 0.5,
                    bgcolor: ({ isActive }) => isActive ? alpha(theme.palette.primary.main, 0.08) : "transparent",
                    color: ({ isActive }) => isActive ? theme.palette.primary.main : "#667085",
                    fontWeight: ({ isActive }) => isActive ? 600 : 400,
                    "&:hover": {
                      bgcolor: alpha(theme.palette.primary.main, 0.05),
                    },
                    transition: "all 0.2s",
                  }}
                >
                  <IconButton color="primary" size="small" sx={{ p: 0.5 }}>
                    {link.icon}
                  </IconButton>
                  <Typography variant="caption" sx={{ fontWeight: "inherit", whiteSpace: "nowrap" }}>
                    {link.label}
                  </Typography>
                </Box>
              </NavLink>
            )
          )}
        </Stack>
      </Paper>

      <Paper
        elevation={0}
        sx={{
          p: 2.5,
          borderRadius: "16px",
          border: `1px solid ${alpha(theme.palette.divider, 0.08)}`,
          bgcolor: "#fff",
          mb: 3,
        }}
      >
        <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 2 }}>
          <Stack direction="row" alignItems="center" spacing={1}>
            <Typography variant="subtitle1" fontWeight={700} sx={{ color: "#1A1A2E" }}>
              How we support {fullName || "Client"}
            </Typography>
            <Chip
              label="Active Plans"
              size="small"
              sx={{
                height: 20,
                fontSize: "0.55rem",
                fontWeight: 500,
                bgcolor: "#EFF8FF",
                color: "#2469F0",
              }}
            />
          </Stack>
          <IconButton
            size="small"
            sx={{
              bgcolor: alpha(theme.palette.primary.main, 0.06),
              "&:hover": { bgcolor: alpha(theme.palette.primary.main, 0.12) },
            }}
          >
            <AddCircleRounded sx={{ fontSize: 20, color: theme.palette.primary.main }} />
          </IconButton>
        </Stack>

        <Divider sx={{ mb: 2.5, opacity: 0.4 }} />

        <Stack spacing={2}>
          <AssessmentsTabs />
          <ConsentTabs />
          <RiskProfileTabs />
          <ActiveCareTabs />
          <MonitorCareTabs />
          <InactiveCareTabs />
        </Stack>
      </Paper>

      <Outlet />

      <Paper
        elevation={0}
        sx={{
          p: 2,
          borderRadius: "16px",
          border: `1px solid ${alpha(theme.palette.divider, 0.08)}`,
          bgcolor: "#FAFBFC",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
        }}
      >
        <Stack direction="row" alignItems="center" spacing={1}>
          <Typography variant="subtitle2" sx={{ color: "#475467", fontWeight: 500 }}>
            Additional Information
          </Typography>
          <Chip
            label="0 notes"
            size="small"
            sx={{
              height: 18,
              fontSize: "0.55rem",
              bgcolor: "#F2F4F7",
              color: "#667085",
            }}
          />
        </Stack>
        <NavLink
          to="#"
          style={{
            textDecoration: "none",
            display: "flex",
            alignItems: "center",
            gap: 4,
            color: theme.palette.primary.main,
            fontSize: "0.8rem",
            fontWeight: 500,
          }}
        >
          <AddCircleRounded sx={{ fontSize: 16 }} />
          Add Notes
          <ChevronRight sx={{ fontSize: 16 }} />
        </NavLink>
      </Paper>
    </Box>
  );
};

export default Assessments;